import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { submitContact } from "@/lib/contact.functions";
import { track } from "@/lib/analytics";
import { SITE_URL } from "@/lib/site";
import {
  PORTRAIT_ALT,
  PORTRAIT_URL,
  breadcrumbs,
  ldScript,
  orgRef,
  pageNode,
  personRef,
  seoMeta,
} from "@/lib/schema";
import { trackMarketingContactSuccess } from "@/lib/marketing/analytics";

const INQUIRY_TYPES = [
  "General", "Booking", "Features", "Production", "Press",
  "Licensing", "Visual collaboration", "Publishing",
  "Business partnerships", "Customer support",
] as const;

const BUDGETED_TYPES = new Set(["Booking", "Features", "Production", "Licensing", "Visual collaboration", "Business partnerships"]);

const TITLE = "Contact Buddha KA | Booking, Press & KAfrequency Inquiries";
const DESCRIPTION =
  "Contact Buddha KA and the KAfrequency team — booking, features, production, licensing, press and media requests, publishing, partnerships, and customer support.";

export const Route = createFileRoute("/contact")({
  component: Contact,
  head: () => ({
    ...seoMeta({
      title: TITLE,
      description: DESCRIPTION,
      path: "/contact",
      image: PORTRAIT_URL,
      imageAlt: PORTRAIT_ALT,
    }),
    scripts: [
      ldScript(
        pageNode({
          type: "ContactPage",
          id: `${SITE_URL}/contact#contactpage`,
          url: `${SITE_URL}/contact`,
          name: "Contact Buddha KA & KAfrequency",
          description: DESCRIPTION,
          mainEntity: orgRef(),
          about: [personRef(), orgRef()],
          extra: { publisher: orgRef() },
        }),
      ),
      ldScript(breadcrumbs([["Contact", "/contact"]])),
    ],
  }),
});


function Contact() {
  const [inquiry, setInquiry] = useState<string>("General");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "loading") return;
    const form = e.currentTarget;
    const data = new FormData(form);

    const payload = {
      inquiry_type: String(data.get("inquiry_type") || "General"),
      name: String(data.get("name") || "").trim(),
      email: String(data.get("email") || "").trim(),
      message: String(data.get("message") || "").trim(),
      budget: String(data.get("budget") || "").trim(),
      timeline: String(data.get("timeline") || "").trim(),
      website: String(data.get("website") || ""), // honeypot
    };

    const newErrors: Record<string, string> = {};
    if (!payload.name) newErrors.name = "Required.";
    if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) newErrors.email = "Enter a valid email.";
    if (!payload.message || payload.message.length < 5) newErrors.message = "Please share the intention of your message.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    setStatus("loading");
    setServerError("");
    try {
      const res = await submitContact({ data: payload });
      track("contact_submit", { inquiry: payload.inquiry_type, forwarded: Boolean(res.forwarded) });
      trackMarketingContactSuccess({ type: payload.inquiry_type, surface: "contact" });
      setStatus("success");
      form.reset();
      setInquiry("General");
    } catch (err) {
      setStatus("error");
      setServerError(err instanceof Error && err.message ? err.message : "Unable to submit. Please try again.");
    }
  }

  return (
    <>
      <section className="relative border-b hairline">
        <div className="absolute inset-0 -z-10 halo opacity-30" aria-hidden />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Contact</p>
          <h1 className="mt-4 font-serif text-5xl sm:text-6xl md:text-7xl">Send a transmission.</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            Every inquiry is read with attention. Choose your intent below so the right hands receive it.
          </p>
          <nav aria-label="Related destinations" className="mt-8 flex flex-wrap gap-2 text-sm">
            <Link to="/press" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
              Buddha KA press kit
            </Link>
            <Link to="/about" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
              Buddha KA biography
            </Link>
            <Link to="/news" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
              Official newsroom
            </Link>
            <Link to="/music" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
              KAfrequency music catalog
            </Link>
          </nav>
        </div>

      </section>

      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        {status === "success" ? (
          <div className="rounded-2xl border-gold border bg-charcoal p-10 text-center">
            <p className="font-serif text-3xl text-gold-luminous">The transmission has been received.</p>
            <p className="mt-4 text-muted-foreground">
              We respond with intention, not automation. Expect a reply within a reasonable window.
            </p>
            <button onClick={() => setStatus("idle")} className="mt-8 rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian">
              Send another
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate className="space-y-6">
            <div>
              <label htmlFor="inquiry_type" className="mb-2 block text-xs uppercase tracking-[0.2em] text-muted-foreground">Inquiry type</label>
              <select
                id="inquiry_type"
                name="inquiry_type"
                value={inquiry}
                onChange={(e) => setInquiry(e.target.value)}
                className="w-full rounded-lg border hairline bg-obsidian px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
              >
                {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-2 block text-xs uppercase tracking-[0.2em] text-muted-foreground">Name</label>
                <input id="name" name="name" className="w-full rounded-lg border hairline bg-obsidian px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" aria-invalid={!!errors.name} />
                {errors.name && <p className="mt-1 text-xs text-red-300">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="email" className="mb-2 block text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</label>
                <input id="email" name="email" type="email" autoComplete="email" className="w-full rounded-lg border hairline bg-obsidian px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" aria-invalid={!!errors.email} />
                {errors.email && <p className="mt-1 text-xs text-red-300">{errors.email}</p>}
              </div>
            </div>

            {BUDGETED_TYPES.has(inquiry) && (
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="budget" className="mb-2 block text-xs uppercase tracking-[0.2em] text-muted-foreground">Budget</label>
                  <input id="budget" name="budget" placeholder="Optional" className="w-full rounded-lg border hairline bg-obsidian px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" />
                </div>
                <div>
                  <label htmlFor="timeline" className="mb-2 block text-xs uppercase tracking-[0.2em] text-muted-foreground">Timeline</label>
                  <input id="timeline" name="timeline" placeholder="Optional" className="w-full rounded-lg border hairline bg-obsidian px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="message" className="mb-2 block text-xs uppercase tracking-[0.2em] text-muted-foreground">Message</label>
              <textarea id="message" name="message" rows={6} className="w-full rounded-lg border hairline bg-obsidian px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" aria-invalid={!!errors.message} />
              {errors.message && <p className="mt-1 text-xs text-red-300">{errors.message}</p>}
            </div>

            {/* Honeypot */}
            <div aria-hidden className="hidden">
              <label htmlFor="website">Website</label>
              <input id="website" name="website" tabIndex={-1} autoComplete="off" />
            </div>

            {serverError && <p role="alert" className="text-sm text-red-300">{serverError}</p>}

            <button
              type="submit"
              disabled={status === "loading"}
              className="rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold disabled:opacity-60"
            >
              {status === "loading" ? "Sending…" : "Send transmission"}
            </button>
          </form>
        )}
      </section>
    </>
  );
}
